import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

import "./Ayuda.css";

/* ============================================================
   API
============================================================ */

const API =
  process.env.NODE_ENV === "production"
    ? "https://gj-frio-industrial-api.onrender.com"
    : "http://localhost:4000";

/* ============================================================
   OBTENER DOCUMENTOS PDF
============================================================ */

const obtenerDocumentos = async () => {
  const respuesta = await axios.get(`${API}/api/documentos`, {
    timeout: 45000,
  });

  return Array.isArray(respuesta.data) ? respuesta.data : [];
};

/* ============================================================
   OBTENER URL COMPLETA DEL DOCUMENTO
============================================================ */

const obtenerUrlDocumento = (documento) => {
  if (!documento?.archivo) {
    return null;
  }

  /* Si Cloudinary u otro servicio ya entrega URL completa */
  if (
    documento.archivo.startsWith("http://") ||
    documento.archivo.startsWith("https://")
  ) {
    return documento.archivo;
  }

  /* Si el backend guarda /documentos/archivo.pdf */
  return `${API}${documento.archivo}`;
};

/* ============================================================
   COMPONENTE
============================================================ */

function Ayuda() {
  const navigate = useNavigate();

  /* ==========================================================
     DOCUMENTOS PDF
  ========================================================== */

  const {
    data: documentos = [],
    isLoading: cargandoDocumentos,
    isFetching: actualizandoDocumentos,
    isError: errorDocumentos,
    error: errorCargaDocumentos,
    refetch: recargarDocumentos,
  } = useQuery({
    queryKey: ["documentos"],
    queryFn: obtenerDocumentos,

    staleTime: 1000 * 60 * 5,
    gcTime: Infinity,

    refetchOnWindowFocus: false,
    refetchOnReconnect: true,

    retry: 2,

    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  /* ==========================================================
     ERROR DOCUMENTOS
  ========================================================== */

  if (errorDocumentos) {
    console.error("Error cargando documentos PDF:", errorCargaDocumentos);
  }

  /* ==========================================================
     VIDEOS
  ========================================================== */

  const videos = [
    {
      id: 1,
      titulo: "Cómo conectar Capacitores para motores de Lavadoras",
      ruta: "https://gj-frio-industrial-api.onrender.com/capacitor.mp4",
    },
    {
      id: 2,
      titulo: "Uso del Multímetro",
      ruta: "https://gj-frio-industrial-api.onrender.com/multimetro.mp4",
    },
    {
      id: 3,
      titulo: "Uso de Protector de Voltaje para tu Refrigerador",
      ruta: "https://gj-frio-industrial-api.onrender.com/protector.mp4",
    },
  ];

  /* ==========================================================
     PREGUNTAS FRECUENTES
  ========================================================== */

  const preguntasFrecuentes = [
    {
      id: 1,
      pregunta: "¿Cómo puedo comprar un producto?",
      respuesta:
        "Busca el producto que necesitas en nuestro catálogo y comunícate con nuestro equipo para confirmar disponibilidad, precio y realizar tu compra.",
    },
    {
      id: 2,
      pregunta: "¿Cómo puedo encontrar un repuesto específico?",
      respuesta:
        "Utiliza el buscador de nuestra página escribiendo el nombre del producto o una palabra relacionada. Si no lo encuentras, comunícate con nosotros para ayudarte a identificarlo.",
    },
    {
      id: 3,
      pregunta: "¿Cómo puedo saber si un producto está disponible?",
      respuesta:
        "La disponibilidad puede cambiar. Te recomendamos confirmar existencias con nuestro equipo antes de realizar la compra.",
    },
    {
      id: 4,
      pregunta: "¿Qué formas de pago tienen disponibles?",
      respuesta:
        "Puedes pagar directamente en nuestro establecimiento o mediante transferencia bancaria. Antes de transferir, confirma con nuestro equipo los datos bancarios vigentes.",
    },
    {
      id: 5,
      pregunta: "¿Realizan envíos a otras ciudades del Ecuador?",
      respuesta:
        "Sí. Puedes comunicarte con nuestro equipo para consultar las opciones de envío disponibles según tu ciudad o provincia.",
    },
    {
      id: 6,
      pregunta: "¿Puedo comprar si me encuentro en otra provincia?",
      respuesta:
        "Sí. Puedes consultar nuestros productos, confirmar disponibilidad y coordinar con nuestro equipo el pago y el envío de tu pedido.",
    },
    {
      id: 7,
      pregunta: "No sé exactamente qué repuesto necesito, ¿pueden ayudarme?",
      respuesta:
        "Sí. Envíanos la información disponible del equipo o repuesto que necesitas y nuestro equipo podrá orientarte.",
    },
    {
      id: 8,
      pregunta: "¿Puedo realizar una consulta por WhatsApp?",
      respuesta:
        "Sí. Puedes comunicarte directamente con nosotros por WhatsApp para consultar productos, precios, disponibilidad y otras dudas.",
    },
    {
      id: 9,
      pregunta: "¿Dónde están ubicados?",
      respuesta:
        "Estamos ubicados en Machala, en Junín entre Marcel Laniado y Kleber Franco. En la sección Contacto puedes consultar nuestra ubicación.",
    },
    {
      id: 10,
      pregunta: "¿Dónde puedo descargar manuales y catálogos?",
      respuesta:
        "En esta misma página encontrarás la sección Manuales y catálogos, donde puedes consultar y descargar los documentos técnicos disponibles.",
    },
  ];

  /* ==========================================================
     ESTADOS
  ========================================================== */

  const [videoSeleccionado, setVideoSeleccionado] = useState(null);

  const [preguntaAbierta, setPreguntaAbierta] = useState(null);

  /* ==========================================================
     ABRIR / CERRAR PREGUNTA
  ========================================================== */

  const togglePregunta = (id) => {
    setPreguntaAbierta((actual) => (actual === id ? null : id));
  };

  /* ==========================================================
     ABRIR PDF
  ========================================================== */

  const abrirDocumento = (documento) => {
    const url = obtenerUrlDocumento(documento);

    if (!url) {
      console.error("El documento no tiene una URL válida:", documento);

      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <div className="ayuda-page">
      <Navbar />

      <main>
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="ayuda-hero">
          <div className="ayuda-container">
            <div className="ayuda-hero-card">
              <div>
                <span className="ayuda-kicker">SOPORTE Y RECURSOS</span>

                <h1>Centro de Ayuda</h1>

                <p>
                  Encuentra tutoriales, manuales, catálogos y guías para
                  resolver tus dudas de forma rápida.
                </p>
              </div>

              <div className="ayuda-hero-icon">
                <i className="bi bi-question-circle-fill"></i>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            PREGUNTAS FRECUENTES
        ===================================================== */}

        <section className="ayuda-faq-section">
          <div className="ayuda-container">
            <div className="ayuda-section-header">
              <div>
                <span className="ayuda-section-kicker">
                  RESOLVEMOS TUS DUDAS
                </span>

                <h2>Preguntas frecuentes</h2>

                <p>
                  Encuentra respuestas rápidas sobre productos, compras, pagos,
                  envíos y atención al cliente.
                </p>
              </div>

              <div className="ayuda-header-line"></div>
            </div>

            <div className="ayuda-faq-list">
              {preguntasFrecuentes.map((item, index) => {
                const abierta = preguntaAbierta === item.id;

                return (
                  <article
                    key={item.id}
                    className={`ayuda-faq-item ${abierta ? "active" : ""}`}
                  >
                    <button
                      type="button"
                      className="ayuda-faq-question"
                      onClick={() => togglePregunta(item.id)}
                      aria-expanded={abierta}
                    >
                      <div className="ayuda-faq-question-left">
                        <span className="ayuda-faq-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="ayuda-faq-icon">
                          <i className="bi bi-question-circle"></i>
                        </span>

                        <strong>{item.pregunta}</strong>
                      </div>

                      <span className="ayuda-faq-toggle">
                        <i
                          className={`bi ${
                            abierta ? "bi-dash-lg" : "bi-plus-lg"
                          }`}
                        ></i>
                      </span>
                    </button>

                    {abierta && (
                      <div className="ayuda-faq-answer">
                        <span>RESPUESTA</span>

                        <p>{item.respuesta}</p>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            <div className="ayuda-faq-contact">
              <div className="ayuda-faq-contact-icon">
                <i className="bi bi-headset"></i>
              </div>

              <div className="ayuda-faq-contact-text">
                <strong>¿No encontraste la respuesta que buscabas?</strong>

                <p>Comunícate con nuestro equipo y con gusto te ayudaremos.</p>
              </div>

              <a
                href="https://wa.link/8proeq"
                target="_blank"
                rel="noopener noreferrer"
                className="ayuda-faq-whatsapp"
              >
                <i className="bi bi-whatsapp"></i>
                Consultar
              </a>
            </div>
          </div>
        </section>

        {/* =====================================================
            COMPRA Y PAGO
        ===================================================== */}

        <section className="ayuda-section">
          <div className="ayuda-container">
            <div className="ayuda-section-header">
              <div>
                <span className="ayuda-section-kicker">FORMAS DE COMPRA</span>

                <h2>Métodos de compra y pago</h2>

                <p>
                  Elige la opción que más te convenga para adquirir nuestros
                  productos.
                </p>
              </div>

              <div className="ayuda-header-line"></div>
            </div>

            <div className="ayuda-payment-grid">
              {/* EFECTIVO */}

              <article className="ayuda-payment-card">
                <div className="ayuda-payment-icon">
                  <i className="bi bi-cash-coin"></i>
                </div>

                <div className="ayuda-payment-content">
                  <span>PAGO DIRECTO</span>

                  <h3>Pago en efectivo</h3>

                  <p>
                    Puedes realizar tu compra en nuestro establecimiento y pagar
                    directamente en efectivo. Visítanos y recibe atención
                    personalizada.
                  </p>

                  <button
                    type="button"
                    className="ayuda-action-button"
                    onClick={() => navigate("/contacto")}
                  >
                    <i className="bi bi-geo-alt-fill"></i>
                    Ver ubicación
                  </button>
                </div>
              </article>

              {/* TRANSFERENCIA */}

              <article className="ayuda-payment-card">
                <div className="ayuda-payment-icon">
                  <i className="bi bi-bank"></i>
                </div>

                <div className="ayuda-payment-content">
                  <span>PAGO BANCARIO</span>

                  <h3>Transferencia bancaria</h3>

                  <p>
                    Realiza una transferencia bancaria directa y guarda el
                    comprobante para enviarlo a nuestro equipo.
                  </p>

                  <button
                    type="button"
                    className="ayuda-action-button"
                    data-bs-toggle="modal"
                    data-bs-target="#bankTransferModal"
                  >
                    <i className="bi bi-info-circle-fill"></i>
                    Ver instrucciones
                  </button>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* =====================================================
            MODAL TRANSFERENCIA
        ===================================================== */}

        <div
          className="modal fade"
          id="bankTransferModal"
          tabIndex="-1"
          aria-labelledby="bankTransferModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered ayuda-bank-modal">
            <div className="modal-content ayuda-modal-content">
              <div className="modal-header ayuda-modal-header">
                <div>
                  <span>TRANSFERENCIA</span>

                  <h5 className="modal-title" id="bankTransferModalLabel">
                    <i className="bi bi-credit-card-2-front"></i>
                    Instrucciones de pago
                  </h5>
                </div>

                <button
                  type="button"
                  className="btn-close btn-close-white"
                  data-bs-dismiss="modal"
                  aria-label="Cerrar"
                ></button>
              </div>

              <div className="modal-body ayuda-modal-body">
                <div className="ayuda-modal-notice">
                  <i className="bi bi-shield-check"></i>

                  <div>
                    <strong>Antes de realizar la transferencia</strong>

                    <p>
                      Confirma los datos bancarios vigentes con nuestro equipo
                      de atención.
                    </p>
                  </div>
                </div>

                <div className="ayuda-instructions">
                  <h6>Pasos para realizar el pago</h6>

                  <div className="ayuda-instruction-item">
                    <span>01</span>
                    <p>Escoge la cuenta bancaria indicada.</p>
                  </div>

                  <div className="ayuda-instruction-item">
                    <span>02</span>
                    <p>Realiza la transferencia correspondiente.</p>
                  </div>

                  <div className="ayuda-instruction-item">
                    <span>03</span>
                    <p>
                      En referencia escribe tu número de orden cuando
                      corresponda.
                    </p>
                  </div>

                  <div className="ayuda-instruction-item">
                    <span>04</span>
                    <p>Guarda el comprobante y envíalo al contacto.</p>
                  </div>
                </div>

                <div className="ayuda-modal-contact">
                  <div>
                    <i className="bi bi-telephone-fill"></i>
                    <span>0939979333 / 0981443171</span>
                  </div>

                  <div>
                    <i className="bi bi-envelope-fill"></i>
                    <span>gonzaloivanal@gmail.com</span>
                  </div>
                </div>
              </div>

              <div className="modal-footer ayuda-modal-footer">
                <button
                  type="button"
                  className="ayuda-modal-close"
                  data-bs-dismiss="modal"
                >
                  <i className="bi bi-x-lg"></i>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            DESCARGAS
        ===================================================== */}

        <section className="ayuda-download-section">
          <div className="ayuda-container">
            <div className="ayuda-section-header">
              <div>
                <span className="ayuda-section-kicker">
                  DOCUMENTACIÓN TÉCNICA
                </span>

                <h2>Manuales y catálogos</h2>

                <p>
                  Descarga catálogos, manuales y guías técnicas disponibles en
                  formato PDF.
                </p>
              </div>

              <div className="ayuda-header-line"></div>
            </div>

            <div className="ayuda-download-box">
              {/* CARGANDO */}

              {cargandoDocumentos && (
                <div className="ayuda-document-status">
                  <div className="spinner-border text-primary">
                    <span className="visually-hidden">Cargando...</span>
                  </div>

                  <div>
                    <strong>Cargando documentos</strong>

                    <p>
                      Estamos consultando los manuales y catálogos disponibles.
                    </p>
                  </div>
                </div>
              )}

              {/* ERROR */}

              {!cargandoDocumentos && errorDocumentos && (
                <div className="ayuda-document-status ayuda-document-status-error">
                  <div className="ayuda-document-status-icon">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                  </div>

                  <div>
                    <strong>No pudimos cargar los documentos</strong>

                    <p>Comprueba tu conexión e intenta nuevamente.</p>
                  </div>

                  <button
                    type="button"
                    className="ayuda-document-retry"
                    onClick={() => recargarDocumentos()}
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                    Reintentar
                  </button>
                </div>
              )}

              {/* SIN DOCUMENTOS */}

              {!cargandoDocumentos &&
                !errorDocumentos &&
                documentos.length === 0 && (
                  <div className="ayuda-document-status">
                    <div className="ayuda-document-status-icon">
                      <i className="bi bi-file-earmark-pdf"></i>
                    </div>

                    <div>
                      <strong>No hay documentos disponibles</strong>

                      <p>
                        Próximamente agregaremos nuevos manuales y catálogos.
                      </p>
                    </div>
                  </div>
                )}

              {/* DOCUMENTOS */}

              {!cargandoDocumentos &&
                !errorDocumentos &&
                documentos.length > 0 && (
                  <>
                    <div className="ayuda-download-summary">
                      <div>
                        <i className="bi bi-files"></i>

                        <span>
                          {documentos.length}{" "}
                          {documentos.length === 1
                            ? "documento disponible"
                            : "documentos disponibles"}
                        </span>
                      </div>

                      {actualizandoDocumentos && (
                        <small>
                          <span
                            className="spinner-border spinner-border-sm"
                            aria-hidden="true"
                          ></span>
                          Actualizando...
                        </small>
                      )}
                    </div>

                    <div className="ayuda-download-grid">
                      {documentos.map((doc, index) => (
                        <article
                          className="ayuda-document-card"
                          key={doc._id || `${doc.nombre}-${index}`}
                        >
                          <div className="ayuda-document-top">
                            <div className="ayuda-pdf-icon">
                              <i className="bi bi-file-earmark-pdf-fill"></i>
                            </div>

                            <span className="ayuda-document-number">
                              {String(index + 1).padStart(2, "0")}
                            </span>
                          </div>

                          <div className="ayuda-document-body">
                            <span>DOCUMENTO PDF</span>

                            <h3>{doc.nombre}</h3>

                            <p>Archivo disponible para consulta y descarga.</p>
                          </div>

                          <button
                            type="button"
                            className="ayuda-download-button"
                            onClick={() => abrirDocumento(doc)}
                            disabled={!doc.archivo}
                          >
                            <i className="bi bi-download"></i>
                            Descargar PDF
                          </button>
                        </article>
                      ))}
                    </div>
                  </>
                )}
            </div>
          </div>
        </section>

        {/* =====================================================
            VIDEOS
        ===================================================== */}

        <section className="ayuda-video-section">
          <div className="ayuda-container">
            <div className="ayuda-section-header">
              <div>
                <span className="ayuda-section-kicker">
                  APRENDE PASO A PASO
                </span>

                <h2>Tutoriales en video</h2>

                <p>
                  Selecciona un tutorial para visualizar el contenido
                  directamente desde la página.
                </p>
              </div>

              <div className="ayuda-header-line"></div>
            </div>

            <div className="ayuda-video-layout">
              {/* LISTADO */}

              <aside className="ayuda-video-list">
                <div className="ayuda-video-list-header">
                  <i className="bi bi-play-btn-fill"></i>

                  <div>
                    <span>TUTORIALES</span>
                    <strong>Selecciona un video</strong>
                  </div>
                </div>

                <div className="ayuda-video-buttons">
                  {videos.map((video, index) => (
                    <button
                      key={video.id}
                      type="button"
                      className={`ayuda-video-button ${
                        videoSeleccionado?.id === video.id ? "active" : ""
                      }`}
                      onClick={() => setVideoSeleccionado(video)}
                    >
                      <span className="ayuda-video-play">
                        <i className="bi bi-play-fill"></i>
                      </span>

                      <div>
                        <small>
                          VIDEO {String(index + 1).padStart(2, "0")}
                        </small>

                        <strong>{video.titulo}</strong>
                      </div>

                      <i className="bi bi-chevron-right ayuda-video-chevron"></i>
                    </button>
                  ))}
                </div>
              </aside>

              {/* REPRODUCTOR */}

              <div className="ayuda-video-player-card">
                {videoSeleccionado ? (
                  <>
                    <div className="ayuda-player-header">
                      <div>
                        <span>REPRODUCIENDO</span>
                        <h3>{videoSeleccionado.titulo}</h3>
                      </div>

                      <i className="bi bi-camera-video-fill"></i>
                    </div>

                    <div className="ayuda-player-video">
                      <video key={videoSeleccionado.id} controls>
                        <source src={videoSeleccionado.ruta} type="video/mp4" />
                        Tu navegador no soporta la reproducción de videos.
                      </video>
                    </div>
                  </>
                ) : (
                  <div className="ayuda-video-empty">
                    <span>
                      <i className="bi bi-play-circle-fill"></i>
                    </span>

                    <h3>Selecciona un tutorial</h3>

                    <p>
                      Elige uno de los videos disponibles para comenzar la
                      reproducción.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <WhatsAppButton />
    </div>
  );
}

export default Ayuda;
